import React from 'react';
import { ShoppingCart, User, Heart, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Template9 = () => {
    const navigate = useNavigate();
    return (
        <div className="bg-white min-h-screen font-sans">
                        {/* Header with Navigation */}
            <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
                <div className="container mx-auto px-4 lg:px-8">
                    <div className="flex justify-between items-center py-4 lg:py-5">
                        <h1 onClick={() => navigate('/template9')} className="text-2xl lg:text-3xl font-bold text-gray-900 cursor-pointer tracking-tight">
                            CHIC BOUTIQUE
                        </h1>
                        <div className="flex items-center gap-4 lg:gap-6">
                            <Search className="w-5 h-5 text-gray-900 cursor-pointer hover:text-gray-600 transition-colors" />
                            <Heart className="w-5 h-5 text-gray-900 cursor-pointer hover:text-gray-600 transition-colors" />
                            <div className="relative cursor-pointer" onClick={() => navigate('/template9/cart')}>
                                <ShoppingCart className="w-5 h-5 text-gray-900 hover:text-gray-600 transition-colors" />
                                <span className="absolute -top-2 -right-2 bg-gray-900 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-medium">0</span>
                            </div>
                            <User className="w-5 h-5 text-gray-900 cursor-pointer hover:text-gray-600 transition-colors" onClick={() => navigate('/template9/profile')} />
                        </div>
                    </div>
                    <nav className="border-t border-gray-200">
                        <div className="flex justify-center items-center gap-8 lg:gap-12 py-4">
                            <button onClick={() => navigate('/template9')} className="text-sm lg:text-base font-medium text-gray-900 hover:text-gray-600 transition-colors uppercase tracking-wide">Home</button>
                            <button onClick={() => navigate('/template9/shop')} className="text-sm lg:text-base font-medium text-gray-900 hover:text-gray-600 transition-colors uppercase tracking-wide">Shop</button>
                            <button onClick={() => navigate('/template9/contact')} className="text-sm lg:text-base font-medium text-gray-900 hover:text-gray-600 transition-colors uppercase tracking-wide">Contact</button>
                            <button onClick={() => navigate('/template9/orders')} className="text-sm lg:text-base font-medium text-gray-900 hover:text-gray-600 transition-colors uppercase tracking-wide">Orders</button>
                        </div>
                    </nav>
                </div>
            </header>
            <main>
                {/* Hero Slider Section - Static Replica */}
                <section className="h-screen w-full relative text-white">
                    <div className="w-full h-full">
                        <img 
                            src="https://placehold.co/1920x1080/4A4A4A/FFFFFF?text=Elegant+Hero+Slider"
                            alt="Hero Banner" 
                            className="absolute z-0 w-full h-full object-cover" 
                        />
                        <div className="absolute z-10 inset-0 bg-black/50"></div>
                        <div className="relative z-20 flex flex-col items-center justify-center h-full text-center p-8">
                            <h1 className="text-5xl md:text-7xl font-extrabold drop-shadow-lg">Elegant Look</h1>
                            <p className="mt-4 text-lg max-w-xl mx-auto drop-shadow-md">Timeless designs for the modern individual.</p>
                            <button className="mt-8 bg-white text-black font-semibold px-6 py-3 rounded-full shadow-lg hover:bg-gray-200 transition-colors">
                                Shop Now
                            </button>
                        </div>
                    </div>
                </section>

                {/* Category Grid Section - Static Replica */}
                <section className="bg-white py-20">
                    <div className="max-w-7xl mx-auto px-4">
                        <div className="text-center mb-12">
                            <h2 className="text-3xl font-bold text-gray-900 tracking-tight">Shop By Category</h2>
                            <p className="text-gray-500 mt-2">Find your perfect look from our curated collections.</p>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                            {[
                                { name: "Indianwear", img: "https://placehold.co/400x500/A0A0A0/FFFFFF?text=Indianwear" },
                                { name: "Westernwear", img: "https://placehold.co/400x500/B0B0B0/FFFFFF?text=Westernwear" },
                                { name: "Blazers", img: "https://placehold.co/400x500/C0C0C0/FFFFFF?text=Blazers" },
                                { name: "Kurtis", img: "https://placehold.co/400x500/D0D0D0/FFFFFF?text=Kurtis" }
                            ].map((cat, index) => (
                                <div key={index} className="group relative overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300">
                                    <img src={cat.img} alt={cat.name} className="w-full h-full object-cover aspect-[4/5] transition-transform duration-500 group-hover:scale-105" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                                    <div className="absolute bottom-0 w-full p-4 text-white">
                                        <h3 className="font-bold text-lg">{cat.name}</h3>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
                
                {/* Lookbook Section - Static Replica */}
                <section className="bg-gray-50 py-20">
                    <div className="max-w-7xl mx-auto px-4">
                        <div className="text-center mb-12">
                            <h2 className="text-3xl font-bold text-gray-900 tracking-tight">The Lookbook</h2>
                            <p className="text-gray-500 mt-2">Explore our hand-picked selections and visual stories.</p>
                        </div>
                        <div className="flex overflow-x-auto snap-x snap-mandatory space-x-6 pb-4">
                            {[
                                { title: "New Arrivals", subtitle: "Fresh styles for a fresh season.", img: "https://placehold.co/600x400/E0D0C0/555555?text=Lookbook+1" },
                                { title: "Holiday Special", subtitle: "Shop our festive collection.", img: "https://placehold.co/600x400/C0C0C0/555555?text=Lookbook+2" },
                                { title: "Summer Style", subtitle: "Light, airy, and chic.", img: "https://placehold.co/600x400/B0B0B0/555555?text=Lookbook+3" },
                                { title: "Winter Trends", subtitle: "Stay warm in style.", img: "https://placehold.co/600x400/A0A0A0/555555?text=Lookbook+4" }
                            ].map((banner, index) => (
                                <div key={index} className="flex-none w-96 snap-start">
                                    <div className="relative overflow-hidden rounded-lg shadow-lg">
                                        <img src={banner.img} alt={banner.title} className="w-full h-full object-cover" />
                                        <div className="absolute inset-0 bg-black/40"></div>
                                        <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-center p-6">
                                            <h3 className="text-2xl font-bold">{banner.title}</h3>
                                            <p className="mt-2 max-w-xs text-sm">{banner.subtitle}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Product Grid */}
                <section className="w-full bg-gray-50 py-16 lg:py-24">
                    <div className="max-w-7xl mx-auto px-4">
                        <div className="text-center mb-12">
                            <h2 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-4">All Products</h2>
                            <p className="text-xl text-gray-600">Discover our latest collection</p>
                        </div>

                        <div className="w-full max-w-none">
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6 lg:gap-8">
                                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(id => (
                                    <div key={id} className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-200 hover:shadow-2xl hover:-translate-y-2 hover:border-gray-900 transition-all duration-300 group">
                                        <div className="aspect-[4/5] overflow-hidden relative bg-gray-50">
                                            <img
                                                src={`https://placehold.co/400x500/F0F0F0/333333?text=Product+${id}`}
                                                alt={`Product ${id}`}
                                                className="object-cover object-center w-full h-full group-hover:scale-110 transition-transform duration-700"
                                            />
                                        </div>
                                        <div className="p-4 lg:p-6">
                                            <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">Product Title {id}</h3>
                                            <p className="text-xl lg:text-2xl font-bold text-gray-900 mb-4">${(69 + id * 10)}.00</p>
                                            <button className="w-full bg-gray-900 text-white py-3 px-6 rounded-lg font-medium hover:bg-gray-800 transition-all duration-300">
                                                Add to Cart
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>
            </main>
                        {/* Footer */}
            <footer className="bg-gray-900 text-white mt-0">
                <div className="container mx-auto px-4 lg:px-8 py-12 lg:py-16">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-8 lg:gap-12">
                        <div>
                            <h3 className="text-2xl font-bold mb-4 tracking-tight">CHIC BOUTIQUE</h3>
                            <p className="text-gray-400 text-sm leading-relaxed">Contemporary design excellence.</p>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wide">Quick Links</h4>
                            <ul className="space-y-2 text-sm">
                                <li><span onClick={() => navigate('/template9')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">Home</span></li>
                                <li><span onClick={() => navigate('/template9/shop')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">Shop</span></li>
                                <li><span onClick={() => navigate('/template9/contact')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">Contact</span></li>
                                <li><span onClick={() => navigate('/template9/orders')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">Orders</span></li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wide">Customer Service</h4>
                            <ul className="space-y-2 text-sm">
                                <li className="text-gray-400">Shipping & Returns</li>
                                <li className="text-gray-400">Privacy Policy</li>
                                <li className="text-gray-400">Terms & Conditions</li>
                                <li className="text-gray-400">FAQ</li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wide">Connect</h4>
                            <p className="text-gray-400 text-sm">hello@chicboutique.com</p>
                        </div>
                    </div>
                    <div className="border-t border-gray-800 mt-12 pt-8">
                        <p className="text-center text-gray-500 text-sm">© 2025 CHIC BOUTIQUE. All rights reserved.</p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Template9;